import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useCurrentUser, cacheCurrentUser } from '@/features/profile/hooks/useCurrentUser'
import userService from '../services/userService'
import type {
  ProfileFormData,
  ProfileFormErrors,
  StudentClass,
  OnboardUserRequest,
} from '../types'
import './ProfileCompletion.css'

// Indian states and union territories list (alphabetically sorted)
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chandigarh',
  'Chhattisgarh', 'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh',
  'Jammu and Kashmir', 'Jharkhand', 'Karnataka', 'Kerala', 'Ladakh',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim',
  'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand',
  'West Bengal',
]

const STUDENT_CLASS_OPTIONS: { value: StudentClass; label: string }[] = [
  { value: 'CLASS_11', label: 'Class 11' },
  { value: 'CLASS_12', label: 'Class 12' },
  { value: 'DROPPER', label: 'Dropper (Gap Year)' },
  { value: 'REPEATER', label: 'Repeater' },
]

const ProfileCompletionPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user: userProfile } = useCurrentUser()
  const redirectTo = (location.state as { redirectTo?: string })?.redirectTo || '/dashboard'

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Pre-fill from existing profile if available
  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: userProfile?.firstName || '',
    lastName: userProfile?.lastName || '',
    studentClass: userProfile?.profileMetadata?.studentClass || '',
    state: userProfile?.profileMetadata?.state || '',
  })

  const [errors, setErrors] = useState<ProfileFormErrors>({})

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name as keyof ProfileFormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const validate = (): boolean => {
    const newErrors: ProfileFormErrors = {}

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }
    if (!formData.studentClass) {
      newErrors.studentClass = 'Please select your class'
    }
    if (!formData.state) {
      newErrors.state = 'Please select your state'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setIsSubmitting(true)
    setError(null)

    try {
      const request: OnboardUserRequest = {
        firstName: formData.firstName,
        ...(formData.lastName && { lastName: formData.lastName }),
        profileMetadata: {
          profileType: 'NEET_STUDENT',
          studentClass: formData.studentClass as StudentClass,
          state: formData.state,
        },
      }

      const response = await userService.onboardUser(request)

      if (response.success) {
        cacheCurrentUser(response.data)
        navigate(redirectTo, { replace: true })
      } else {
        throw new Error(response.message || 'Failed to save profile')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save profile'
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderForm = () => (
    <div className="form-step">
      <h3>Let's get started</h3>
      <p className="step-description">
        Please provide the following required information to set up your account.
      </p>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="firstName">First Name *</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            placeholder="Enter your first name"
          />
          {errors.firstName && <span className="field-error">{errors.firstName}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            placeholder="Enter your last name"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="studentClass">Current Class *</label>
        <select
          id="studentClass"
          name="studentClass"
          value={formData.studentClass}
          onChange={handleInputChange}
        >
          <option value="">Select your class</option>
          {STUDENT_CLASS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {errors.studentClass && (
          <span className="field-error">{errors.studentClass}</span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="state">State *</label>
        <select
          id="state"
          name="state"
          value={formData.state}
          onChange={handleInputChange}
        >
          <option value="">Select your state</option>
          {INDIAN_STATES.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </select>
        {errors.state && <span className="field-error">{errors.state}</span>}
      </div>
    </div>
  )

  return (
    <div className="profile-completion-container">
      <div className="profile-completion-card">
        <div className="profile-header">
          <h1>My NEET Buddy</h1>
          <h2>Complete Your Profile</h2>
        </div>

        {error && <div className="error-message">{error}</div>}

        {renderForm()}

        <div className="form-actions">
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Complete Profile'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ProfileCompletionPage
